import { useCallback, useRef, type ReactNode } from "react";
import { StyledDraggableOverlay } from "./index.styled";

export type OverlayPosition = { x: number; y: number };

interface DraggableQuestionOverlayProps {
  children: ReactNode;
  position: OverlayPosition;
  onPositionChange: (pos: OverlayPosition) => void;
  containerRef: React.RefObject<HTMLElement | null>;
  disabled?: boolean;
  width?: number | string;
  zIndex?: number;
  excludeSelector?: string;
  /** When "vertical", only Y position changes (X stays fixed). */
  axis?: "both" | "vertical";
  /** Keep this fraction of container height as bottom inset when clamping Y. */
  bottomInsetRatio?: number;
}

const DraggableQuestionOverlay = ({
  children,
  position,
  onPositionChange,
  containerRef,
  disabled = false,
  width,
  zIndex = 2,
  excludeSelector = "[data-no-drag]",
  axis = "both",
  bottomInsetRatio = 0,
}: DraggableQuestionOverlayProps) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);

  const clampPosition = useCallback(
    (x: number, y: number): OverlayPosition => {
      const container = containerRef.current;
      const overlay = overlayRef.current;
      if (!container || !overlay) return { x, y };

      const maxX = Math.max(0, container.clientWidth - overlay.offsetWidth);
      const bottomInset = container.clientHeight * bottomInsetRatio;
      const maxY = Math.max(
        0,
        container.clientHeight - overlay.offsetHeight - bottomInset,
      );
      const clampedY = Math.min(maxY, Math.max(0, y));
      if (axis === "vertical") {
        return { x: position.x, y: clampedY };
      }
      return {
        x: Math.min(maxX, Math.max(0, x)),
        y: clampedY,
      };
    },
    [containerRef, axis, position.x, bottomInsetRatio],
  );

  const onPointerDown = (e: React.PointerEvent) => {
    if (disabled) return;
    if (
      excludeSelector &&
      (e.target as Element).closest(excludeSelector)
    ) {
      return;
    }
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      originX: position.x,
      originY: position.y,
    };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current || disabled) return;
    e.preventDefault();
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    onPositionChange(
      clampPosition(
        axis === "vertical"
          ? dragRef.current.originX
          : dragRef.current.originX + dx,
        dragRef.current.originY + dy,
      ),
    );
  };

  const onPointerUp = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  return (
    <StyledDraggableOverlay
      ref={overlayRef}
      style={
        axis === "vertical"
          ? {
              top: position.y,
              left: "50%",
              transform: "translateX(-50%)",
              width: width ?? "calc(100% - 12px)",
              zIndex,
            }
          : {
              left: position.x,
              top: position.y,
              width: width ?? undefined,
              zIndex,
            }
      }
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      $disabled={disabled}
      $axis={axis}
      role="presentation"
    >
      {children}
    </StyledDraggableOverlay>
  );
};

export default DraggableQuestionOverlay;
